import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import API_URL from '../api';
import logoMark from '../assets/images/logo.jpg';
import ApplicationCard from '../applications/ApplicationCard';
import {
  APPLICATION_STATUS_STYLE,
  applicationRef,
  companyOf,
  fetchApplications as fetchApplicationsApi,
  jobIdOf,
  submitApplication,
} from '../applications/api';
import {
  ActionButton,
  AnimatedProgress,
  EmptyState,
  MatchScore,
  MetricTile,
  Modal,
  Reveal,
  SkeletonGrid,
  Stagger,
  TabNav,
  WhyMatch,
} from '../ui';

// ─── Constants ───────────────────────────────────────────────────────────────

const NOTICE_PERIOD_OPTIONS = ['Immediate', '7 days', '1 month', '3 months'];
const WORK_MODE_OPTIONS = ['Onsite', 'Hybrid', 'Remote'];
const MAX_COMP = 50000;
const COMP_STEP = 1000;

// The four numbers a student actually wants from their job search, in the
// order the pipeline runs. Counts are derived from application status, so
// nothing here is an estimate.
const APPLICATION_SUMMARY = [
  {
    key: 'total', label: 'Applications', hint: 'Submitted', tone: 'primary',
    icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>,
  },
  {
    key: 'shortlisted', label: 'Shortlisted', hint: 'Recruiter interest', tone: 'success',
    icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>,
  },
  {
    key: 'interviews', label: 'Interviews', hint: 'Scheduled', tone: 'warning',
    icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  },
  {
    key: 'offers', label: 'Offers', hint: 'Selected', tone: 'success',
    icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l2.9 6.3 6.6.8-4.9 4.6 1.3 6.6L12 17.1 6.1 20.3l1.3-6.6L2.5 9.1l6.6-.8z"/></svg>,
  },
];

// Scroll the resume upload zone into view and open the file picker. Module
// level because it only reaches for the DOM — both the blocking "re-upload"
// modal and the ?resume=1 deep link from the job page land here.
function openResumeUpload() {
  // Wait for the profile tab to mount before scrolling to / clicking into it.
  setTimeout(() => {
    document.getElementById('resume-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    document.getElementById('resume-file')?.click();
  }, 150);
}

// The dashboard surfaces a ?tab= deep link may name. Anything else falls back
// to the front page rather than rendering an empty body.
const DASHBOARD_TABS = ['dashboard', 'opportunities', 'applications', 'assignments', 'profile'];

const SKILLS_FALLBACK = [
  'Python','JavaScript','TypeScript','React','Node.js','Java','C++','Go','Rust','Machine Learning',
  'Deep Learning','Data Analysis','TensorFlow','PyTorch','AWS','Docker','Kubernetes','MongoDB',
  'PostgreSQL','Flutter','Android','iOS','Next.js','Django','Flask','GraphQL','Blockchain',
  'Product Management','UI/UX Design','Figma','Video Editing','Digital Marketing','SEO',
  'Content Writing','Financial Modeling','Project Management','Public Speaking',
];

// ─── Pure helpers ─────────────────────────────────────────────────────────────

function formatComp(val) {
  if (!val && val !== 0) return '—';
  if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
  return `₹${(val / 1000).toFixed(0)}k`;
}

function formatStipend(stipend) {
  if (!stipend) return 'Stipend not disclosed';
  const { min, max } = stipend;
  if (!min && !max) return 'Stipend not disclosed';
  if (min && max) return `₹${(min / 1000).toFixed(0)}k – ₹${(max / 1000).toFixed(0)}k /mo`;
  if (min) return `₹${(min / 1000).toFixed(0)}k+ /mo`;
  return `Up to ₹${(max / 1000).toFixed(0)}k /mo`;
}

function completionScore(data) {
  const checks = [
    !!(data.bio), !!(data.phone), !!(data.resume_url),
    !!(data.skills && data.skills.length > 0),
    !!(data.notice_period),
    !!(Array.isArray(data.work_mode) ? data.work_mode.length > 0 : data.work_mode),
    !!(data.weekly_commitment), !!(data.monthly_rate_min),
    !!(data.linkedin_url || data.github_url),
  ];
  return Math.round((checks.filter(Boolean).length / checks.length) * 100);
}

function normalizeWorkMode(m) {
  return (m || '').toLowerCase().replace(/[-\s]/g, '');
}

function calcMatchPct(job, student) {
  if (!student || !job) return 0;
  let score = 0, max = 0;

  // Skill overlap — 50 pts
  max += 50;
  const stuSkills = (student.skills || []).map(s => (s.name || s).toLowerCase());
  const jobSkills = (job.skills || []).map(s => s.toLowerCase());
  if (jobSkills.length > 0) {
    const matched = jobSkills.filter(s => stuSkills.some(ss => ss === s || ss.includes(s) || s.includes(ss)));
    score += (matched.length / jobSkills.length) * 50;
  } else {
    score += 50;
  }

  // Work mode — 30 pts (normalize 'Onsite'/'On-site' → 'onsite' before comparing)
  max += 30;
  const stuModes = (student.work_mode || []).map(normalizeWorkMode);
  const jobMode = normalizeWorkMode(job.work_mode);
  if (!jobMode || stuModes.includes(jobMode)) score += 30;

  // No college restriction — 20 pts
  max += 20;
  if (!job.preferred_colleges || job.preferred_colleges.length === 0) {
    score += 20;
  } else {
    const college = (student.college || student.iit_campus || '').toLowerCase();
    if (job.preferred_colleges.some(c => c.toLowerCase().includes(college) || college.includes(c.toLowerCase()))) score += 20;
  }

  return Math.round((score / max) * 100);
}

function getMatchReasons(job, student) {
  if (!student || !job) return [];
  const reasons = [];
  const college = student.college || student.iit_campus;
  if (college) reasons.push(`${college} Student`);

  const stuSkills = (student.skills || []).map(s => s.name || s);
  const jobSkills = (job.skills || []);
  const matched = jobSkills.filter(s => stuSkills.some(ss => ss.toLowerCase() === s.toLowerCase()));
  if (matched.length > 0) reasons.push(`${matched[0]} Skill Match`);
  if (matched.length > 1) reasons.push(`+${matched.length - 1} more skill${matched.length > 2 ? 's' : ''}`);

  const stuModes = (student.work_mode || []);
  if (job.work_mode && stuModes.map(m => m.toLowerCase()).includes(job.work_mode.toLowerCase())) {
    reasons.push(`Open To ${job.work_mode} Work`);
  }
  return reasons;
}

function daysAgoText(dateStr) {
  const d = Math.floor((Date.now() - new Date(dateStr)) / 86400000);
  if (d === 0) return 'Today';
  if (d === 1) return 'Yesterday';
  return `${d}d ago`;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StipendSlider({ label, value, min, max, step, onChange, pct }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.01em' }}>{label}</span>
        <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--primary)' }}>{formatComp(value)}<span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)', marginLeft: 4 }}>/ mo</span></span>
      </div>
      <input type="range" className="stipend-slider" style={{ '--pct': `${pct}%` }}
        min={min} max={max} step={step} value={value} onChange={onChange} />
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
        <span>₹0</span><span>₹50k</span>
      </div>
    </div>
  );
}

function CompensationRange({ minVal, maxVal, onMinChange, onMaxChange }) {
  return (
    <div className="comp-range">
      <div className="comp-range-header">
        <span className="comp-range-display">
          {formatComp(minVal)} <span>to</span> {formatComp(maxVal)}<span style={{ marginLeft: 6 }}>/ month</span>
        </span>
      </div>
      <StipendSlider label="Minimum" value={minVal} pct={(minVal / MAX_COMP) * 100}
        min={0} max={MAX_COMP} step={COMP_STEP}
        onChange={e => onMinChange(Math.min(parseInt(e.target.value), maxVal - COMP_STEP))} />
      <StipendSlider label="Maximum" value={maxVal} pct={(maxVal / MAX_COMP) * 100}
        min={0} max={MAX_COMP} step={COMP_STEP}
        onChange={e => onMaxChange(Math.max(parseInt(e.target.value), minVal + COMP_STEP))} />
    </div>
  );
}

/* Bookmark glyph — outline when unsaved, filled once saved, so the
   state change reads at a glance instead of as a label swap. */
function SaveIcon({ filled }) {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function JobCard({ job, student, onApply, onSave, onUnsave, isSaved, appliedStatus, applying, onViewDetails, showMatch = true }) {
  const match = showMatch ? calcMatchPct(job, student) : 0;
  const reasons = showMatch ? getMatchReasons(job, student) : [];
  const company = job.posted_by?.company_name || job.posted_by?.name || 'Company';
  const stuSkillNames = (student?.skills || []).map(s => (s.name || s).toLowerCase());
  const deadline = job.application_deadline
    ? new Date(job.application_deadline).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
    : null;

  /* 'Applied' is the fresh-application state and belongs on the button so
     the transition finishes where it started. Anything further along the
     pipeline is pipeline news, and gets its own status pill. */
  const inPipeline = appliedStatus && appliedStatus !== 'Applied';
  const applyState = appliedStatus === 'Applied' ? 'done' : applying ? 'pending' : 'idle';

  return (
    <div className="sd-job-card ui-lift">
      {/* Header — company, role, and why it is in front of you */}
      <div className="sd-job-card__head">
        <div className="sd-job-card__logo">{company[0]?.toUpperCase()}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="sd-job-card__title">{job.title}</div>
          <div className="sd-job-card__company">{company}</div>
        </div>
        {showMatch && <MatchScore value={match} />}
      </div>

      {/* Stipend leads: it is the fact students scan for first. */}
      <div className="sd-job-card__pay">{formatStipend(job.stipend)}</div>

      {/* Facts */}
      <div className="sd-job-card__meta">
        <span className="sd-tag sd-tag--primary">{job.position_type}</span>
        <span className="sd-tag">{job.work_mode}</span>
        {job.location && <span className="sd-tag">{job.location}</span>}
        {deadline && <span className="sd-tag sd-tag--due">Due {deadline}</span>}
      </div>

      {/* Skills — the ones the student already has are highlighted */}
      {job.skills?.length > 0 && (
        <div className="sd-job-card__meta">
          {job.skills.slice(0, 5).map(s => (
            <span key={s} className={`sd-tag${stuSkillNames.includes(s.toLowerCase()) ? ' sd-tag--have' : ''}`}>{s}</span>
          ))}
          {job.skills.length > 5 && <span className="sd-tag sd-tag--ghost">+{job.skills.length - 5}</span>}
        </div>
      )}

      {showMatch && reasons.length > 0 && (
        <WhyMatch reasons={reasons} style={{ marginTop: 2 }} />
      )}

      <div style={{ flex: 1 }} />

      {/* Footer */}
      <div className="sd-job-card__foot">
        <span className="sd-job-card__age">{daysAgoText(job.created_at)}</span>
        <div className="sd-job-card__actions">
          <button type="button" className="sd-ghost-btn ui-press" onClick={() => onViewDetails(job)}>View</button>
          <button
            type="button"
            className={`sd-ghost-btn ui-press${isSaved ? ' is-on' : ''}`}
            onClick={() => isSaved ? onUnsave(job._id) : onSave(job._id)}
            aria-pressed={isSaved}
          >
            <SaveIcon filled={isSaved} />
            {isSaved ? 'Saved' : 'Save'}
          </button>
          {inPipeline
            ? <span className="sd-status-pill" style={APPLICATION_STATUS_STYLE[appliedStatus]}>{appliedStatus}</span>
            : <ActionButton
                state={applyState}
                labels={{ idle: 'Apply Now', pending: 'Applying…', done: 'Applied' }}
                onClick={() => onApply(job._id)}
              />
          }
        </div>
      </div>
    </div>
  );
}

function JobDetailModal({ job: activeJob, student, onClose, onApply, onSave, onUnsave, isSaved, appliedStatus, applying }) {
  /* The last opened job is kept so the dialog still has something to draw
     while it plays its exit — `activeJob` is null from the click that
     dismissed it, but the card is on screen for another 150ms. */
  const lastJob = useRef(activeJob);
  if (activeJob) lastJob.current = activeJob;
  const job = lastJob.current;

  if (!job) return null;
  const company = job.posted_by?.company_name || job.posted_by?.name || 'Company';
  const match = calcMatchPct(job, student);
  const reasons = getMatchReasons(job, student);
  const stuSkillNames = (student?.skills || []).map(s => (s.name || s).toLowerCase());

  return (
    <Modal open={!!activeJob} onClose={onClose} maxWidth={660} cardStyle={{ padding: 32 }}>
      <>
        {/* Header */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 20 }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: 'var(--primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: 'var(--primary)', fontSize: 20, flexShrink: 0 }}>
            {company[0]?.toUpperCase()}
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: '0 0 4px', fontSize: 20 }}>{job.title}</h2>
            <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>{company}</div>
          </div>
          <MatchScore value={match} size="lg" />
        </div>

        {/* Badges */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
          <span style={{ background: '#EEF2FF', color: 'var(--primary)', fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 20 }}>{job.position_type}</span>
          <span style={{ background: '#F3F4F6', color: '#4B5563', fontSize: 12, padding: '3px 10px', borderRadius: 20 }}>{job.work_mode}</span>
          {job.location && <span style={{ background: '#F3F4F6', color: '#4B5563', fontSize: 12, padding: '3px 10px', borderRadius: 20 }}>📍 {job.location}</span>}
          {job.role_category && <span style={{ background: '#F3F4F6', color: '#4B5563', fontSize: 12, padding: '3px 10px', borderRadius: 20 }}>{job.role_category}</span>}
        </div>

        <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--primary)', marginBottom: 14 }}>{formatStipend(job.stipend)}</div>

        {/* Why you match */}
        <WhyMatch reasons={reasons} style={{ marginBottom: 16, padding: '12px 16px' }} />

        {/* Description */}
        {job.description && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--text-muted)', marginBottom: 8 }}>About this role</div>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.7 }}>{job.description}</p>
          </div>
        )}

        {/* Responsibilities */}
        {job.responsibilities?.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--text-muted)', marginBottom: 8 }}>Responsibilities</div>
            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 14, color: 'var(--text-secondary)', lineHeight: 2 }}>
              {job.responsibilities.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          </div>
        )}

        {/* Skills */}
        {job.skills?.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--text-muted)', marginBottom: 8 }}>Required Skills</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {job.skills.map(s => {
                const isMatch = stuSkillNames.includes(s.toLowerCase());
                return <span key={s} style={{ background: isMatch ? 'var(--primary-light)' : '#F3F4F6', color: isMatch ? 'var(--primary)' : '#6B7280', fontSize: 13, padding: '4px 12px', borderRadius: 20, fontWeight: isMatch ? 600 : 400 }}>{s}</span>;
              })}
            </div>
          </div>
        )}

        {/* Details grid */}
        {(job.duration_months || job.start_date || job.application_deadline || job.num_openings) && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--text-muted)', marginBottom: 8 }}>Details</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
              {job.duration_months && <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Duration: <strong>{job.duration_months} months</strong></div>}
              {job.num_openings && <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Openings: <strong>{job.num_openings}</strong></div>}
              {job.start_date && <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Starts: <strong>{new Date(job.start_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</strong></div>}
              {job.application_deadline && <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Apply by: <strong style={{ color: '#C2410C' }}>{new Date(job.application_deadline).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</strong></div>}
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
          {appliedStatus && appliedStatus !== 'Applied'
            ? <div style={{ flex: 1, textAlign: 'center', padding: 10, borderRadius: 10, fontWeight: 600, ...APPLICATION_STATUS_STYLE[appliedStatus] }}>{appliedStatus}</div>
            : <ActionButton
                size="md"
                state={appliedStatus === 'Applied' ? 'done' : applying ? 'pending' : 'idle'}
                labels={{ idle: 'Apply Now', pending: 'Checking resume…', done: 'Applied' }}
                onClick={() => onApply(job._id)}
                style={{ flex: 1 }}
              />
          }
          <button
            type="button"
            className={`sd-ghost-btn sd-ghost-btn--lg ui-press${isSaved ? ' is-on' : ''}`}
            onClick={() => isSaved ? onUnsave(job._id) : onSave(job._id)}
            aria-pressed={isSaved}
          >
            <SaveIcon filled={isSaved} />
            {isSaved ? 'Saved' : 'Save'}
          </button>
        </div>
      </>
    </Modal>
  );
}

function EmptyJobsState({ onInvite }) {
  return (
    <EmptyState
      art="briefcase"
      title="No opportunities available yet"
      body="Recruiters post new roles here every week. Know a company that should be hiring from your campus?"
      action={onInvite}
      actionLabel="Invite Recruiters"
    />
  );
}

// ─── Section header for dashboard feed ───────────────────────────────────────
// While loading it holds the shape of what is coming rather than showing a
// spinner, so arriving content does not shove the rest of the feed around.
function FeedSection({ title, subtitle, actionLabel, onAction, children, loading, skeleton }) {
  return (
    <Reveal as="section" className="sd-feed">
      <div className="sd-feed__head">
        <h3>{title}</h3>
        {onAction && (
          <button type="button" className="sd-feed__action" onClick={onAction}>
            {actionLabel || 'View all →'}
          </button>
        )}
      </div>
      {subtitle && <p className="sd-feed__sub">{subtitle}</p>}
      {loading ? (skeleton || <SkeletonGrid count={4} />) : children}
    </Reveal>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function StudentDashboard({ onLogout }) {
  // Profile state
  const [student, setStudent] = useState(null);
  const [formData, setFormData] = useState({});
  const [availableSkills, setAvailableSkills] = useState([]);
  const [skillSearch, setSkillSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [savingSkills, setSavingSkills] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deletingResume, setDeletingResume] = useState(false);
  const [pendingSkillNames, setPendingSkillNames] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [requestingVerification, setRequestingVerification] = useState(false);
  const [profileViewCount, setProfileViewCount] = useState(0);
  const [profileViewers, setProfileViewers] = useState([]);
  const [viewersUnlocked, setViewersUnlocked] = useState(false);

  // UI state
  //
  // Deep links land on a named surface. A candidate sent here from the job page
  // — "complete your profile", "re-upload your resume", "view my applications"
  // — has to arrive where they were pointed, not on the dashboard front page.
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const requested = searchParams.get('tab');
    return DASHBOARD_TABS.includes(requested) ? requested : 'dashboard';
  });
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showViewersPaywall, setShowViewersPaywall] = useState(false);
  const [toast, setToast] = useState(null);

  // Jobs / opportunities state
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [savedJobs, setSavedJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [applyingTo, setApplyingTo] = useState(null);
  // Set when the server rejects an application because the stored resume could
  // not be fetched — drives the blocking "Re-upload Resume" modal.
  const [resumeBlock, setResumeBlock] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);

  // Opportunities page filters
  const [oppSubTab, setOppSubTab] = useState('all');
  const [filterType, setFilterType] = useState('');
  const [filterMode, setFilterMode] = useState('');

  // Assignments state
  const [assignments, setAssignments] = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [expandedAssignments, setExpandedAssignments] = useState({});
  const [submitModal, setSubmitModal] = useState({ open: false, assignment: null });
  const [submitForm, setSubmitForm] = useState({ link_url: '', comments: '' });
  const [submitFile, setSubmitFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [unreadMsgCount, setUnreadMsgCount] = useState(0);

  // Autosave state
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saved'|'pending'|'saving'|'error'|'retrying'
  const [onboardingStep, setOnboardingStep] = useState(0);
  const autosaveTimerRef = useRef(null);
  const retryTimerRef = useRef(null);
  const retryCountRef = useRef(0);
  const formDataRef = useRef({});
  const skillAutosaveTimerRef = useRef(null);
  const pendingSkillNamesRef = useRef([]);

  const completionShownRef = useRef(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  // ── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => {
    fetchProfile();
    fetchAvailableSkills();
    fetchProfileViews();
    fetchJobs();
    fetchApplications();
    fetchSavedJobs();
    fetchAssignments();
    fetchUnreadMsgCount();
    const interval = setInterval(fetchUnreadMsgCount, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (formData.skills) setPendingSkillNames(formData.skills.map(s => s.name));
  }, [formData.skills]);

  // Keep formDataRef in sync for autosave closure
  useEffect(() => { formDataRef.current = formData; }, [formData]);
  useEffect(() => { pendingSkillNamesRef.current = pendingSkillNames; }, [pendingSkillNames]);

  // Warn on unsaved changes
  useEffect(() => {
    const handler = e => {
      if (saveStatus === 'pending' || saveStatus === 'saving') {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [saveStatus]);

  useEffect(() => {
    const handler = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowProfileDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* ?resume=1 — sent by the job page when an apply was blocked on the resume.
     The flag is consumed on arrival so a refresh does not re-open the file
     picker, which is also what stops this effect re-running. */
  useEffect(() => {
    if (searchParams.get('resume') !== '1') return;
    const remaining = new URLSearchParams(searchParams);
    remaining.delete('resume');
    setSearchParams(remaining, { replace: true });
    setActiveTab('profile');
    openResumeUpload();
  }, [searchParams, setSearchParams]);

  const pct = completionScore(formData);

  useEffect(() => {
    if (!student) return;
    const key = `pcm_${student._id}`;
    if (pct >= 80 && !completionShownRef.current && !localStorage.getItem(key)) {
      completionShownRef.current = true;
      localStorage.setItem(key, '1');
      setShowCompletionModal(true);
    }
  }, [pct, student]);

  // ── Fetch functions ────────────────────────────────────────────────────────

  const fetchUnreadMsgCount = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/messages/unread-count`, { headers: { Authorization: `Bearer ${token}` } });
      setUnreadMsgCount(res.data.count || 0);
    } catch { /* silent */ }
  };

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/students/profile`, { headers: { Authorization: `Bearer ${token}` } });
      setStudent(res.data);
      setFormData(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchAvailableSkills = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/skills`);
      if (res.data && res.data.length > 0) {
        setAvailableSkills(res.data);
      } else {
        await axios.post(`${API_URL}/api/skills/seed`);
        const seeded = await axios.get(`${API_URL}/api/skills`);
        setAvailableSkills(seeded.data.length > 0 ? seeded.data : SKILLS_FALLBACK.map((name, i) => ({ _id: i, name })));
      }
    } catch {
      setAvailableSkills(SKILLS_FALLBACK.map((name, i) => ({ _id: i, name })));
    }
  };

  const fetchProfileViews = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/students/profile/views`, { headers: { Authorization: `Bearer ${token}` } });
      setProfileViewCount(res.data.count || 0);
      setProfileViewers(res.data.views || []);
      const key = `viewers_unlocked_${localStorage.getItem('userId') || ''}`;
      setViewersUnlocked(!!localStorage.getItem(key));
    } catch { setProfileViewCount(0); }
  };

  const fetchJobs = async () => {
    setJobsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/jobs/public`);
      setJobs(res.data);
    } catch (err) { console.error(err); }
    finally { setJobsLoading(false); }
  };

  /* `ensure` is an application the server has already confirmed exists — the
     one POST /api/applications just returned. A list read that comes back
     without it is stale, not authoritative, so it is folded back in rather
     than allowed to erase a submission the candidate was just told went
     through. Without this the Applications tab can go empty immediately after
     a successful apply, which is the failure that was reported. */
  const fetchApplications = async ({ ensure } = {}) => {
    try {
      const list = await fetchApplicationsApi(token);
      const missing = ensure && !list.some(a => String(a._id) === String(ensure._id));
      setApplications(missing ? [ensure, ...list] : list);
    } catch (err) { console.error(err); }
  };

  const fetchSavedJobs = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/saved-jobs`, { headers: { Authorization: `Bearer ${token}` } });
      setSavedJobs(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchAssignments = async () => {
    setAssignmentsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/assignments/student`, { headers: { Authorization: `Bearer ${token}` } });
      setAssignments(res.data);
    } catch { /* silent */ }
    finally { setAssignmentsLoading(false); }
  };

  const handleSubmitAssignment = async (e) => {
    e.preventDefault();
    if (!submitForm.link_url && !submitFile) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      if (submitFile) fd.append('file', submitFile);
      if (submitForm.link_url) fd.append('link_url', submitForm.link_url);
      if (submitForm.comments) fd.append('comments', submitForm.comments);
      await axios.post(`${API_URL}/api/assignments/${submitModal.assignment._id}/submit`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      showToast('Assignment submitted!');
      setSubmitModal({ open: false, assignment: null });
      setSubmitForm({ link_url: '', comments: '' });
      setSubmitFile(null);
      fetchAssignments();
    } catch (err) {
      showToast(err.response?.data?.error || 'Submission failed', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Job action handlers ───────────────────────────────────────────────────

  const handleApply = async (jobId) => {
    setApplyingTo(jobId);
    // The server validates that the stored resume is actually fetchable before
    // it creates the application, so this single call covers both steps —
    // success is only reported once validation AND insertion have succeeded.
    const result = await submitApplication(token, jobId);
    setApplyingTo(null);

    if (result.ok) {
      // The created application comes back populated, so the Applications tab
      // is correct the moment this returns — the list refresh below is a
      // reconciliation, not the thing the student is waiting on.
      addApplication(result.application);
      showToast(`Application submitted — ${applicationRef(result.application)}`);
      fetchApplications({ ensure: result.application });
      return;
    }

    // Already applied: nothing failed, the list was just stale.
    if (result.code === 'ALREADY_APPLIED' && result.application) {
      addApplication(result.application);
      showToast('You have already applied to this role');
      return;
    }

    // A broken resume is not a toast-and-forget failure: the student cannot
    // apply anywhere until they re-upload, so surface it as a blocking modal
    // with a direct route to the upload zone.
    if (result.code === 'RESUME_INACCESSIBLE' || result.code === 'RESUME_MISSING') {
      setResumeBlock({ message: result.message, code: result.code });
      setSelectedJob(null);
      return;
    }

    showToast(result.message, 'error');
  };

  /* Fold one application into the list without waiting for a refetch, keeping
     it deduplicated by id and newest-first — the order GET /api/applications
     returns. A submitted application has to be visible immediately; it used to
     appear only if the follow-up list fetch happened to succeed. */
  const addApplication = (application) => {
    if (!application?._id) return;
    setApplications(prev => [
      application,
      ...prev.filter(a => String(a._id) !== String(application._id)),
    ]);
  };

  // Send the student straight to the resume upload zone and open the picker.
  const goToResumeUpload = () => {
    setResumeBlock(null);
    setActiveTab('profile');
    openResumeUpload();
  };

  const handleSaveJob = async (jobId) => {
    try {
      await axios.post(`${API_URL}/api/saved-jobs`, { job_id: jobId }, { headers: { Authorization: `Bearer ${token}` } });
      fetchSavedJobs();
    } catch (err) {
      if (err.response?.status !== 400) showToast('Failed to save', 'error');
    }
  };

  const handleUnsaveJob = async (jobId) => {
    try {
      await axios.delete(`${API_URL}/api/saved-jobs/${jobId}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchSavedJobs();
    } catch { showToast('Failed to remove', 'error'); }
  };

  // ── Derived data helpers ──────────────────────────────────────────────────

  const getApplicationStatus = (jobId) =>
    applications.find(a => jobIdOf(a) === (jobId ? String(jobId) : null))?.status || null;

  const isJobSaved = (jobId) => savedJobs.some(s => {
    const id = typeof s.job_id === 'object' ? s.job_id?._id : s.job_id;
    return id?.toString() === jobId?.toString();
  });

  const recommendedJobs = [...jobs]
    .map(j => ({ ...j, _m: calcMatchPct(j, student) }))
    .sort((a, b) => b._m - a._m)
    .slice(0, 4);

  const savedJobObjects = savedJobs
    .filter(s => s.job_id && typeof s.job_id === 'object')
    .map(s => s.job_id);

  const countByStatus = (status) => applications.filter(a => a.status === status).length;
  const applicationSummary = {
    total: applications.length,
    shortlisted: countByStatus('Shortlisted'),
    interviews: countByStatus('Interview Scheduled'),
    offers: countByStatus('Selected'),
  };

  const getFilteredJobs = () => {
    let list = [...jobs];
    if (filterType) list = list.filter(j => j.position_type === filterType);
    if (filterMode) list = list.filter(j => j.work_mode === filterMode);
    if (oppSubTab === 'recommended') {
      return list.map(j => ({ ...j, _m: calcMatchPct(j, student) })).sort((a, b) => b._m - a._m);
    }
    if (oppSubTab === 'newest') return list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return list;
  };

  // ── Profile edit handlers ─────────────────────────────────────────────────

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  // ── Autosave (returning user mode) ────────────────────────────────────────

  const isOnboarding = student && !localStorage.getItem(`ob_${student._id}`) && completionScore(student) < 80;

  const scheduleAutosave = () => {
    if (isOnboarding) return;
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    setSaveStatus('pending');
    autosaveTimerRef.current = setTimeout(performAutosave, 2500);
  };

  const performAutosave = async (isRetry = false) => {
    setSaveStatus(isRetry ? 'retrying' : 'saving');
    try {
      await axios.put(`${API_URL}/api/students/profile`, formDataRef.current, { headers: { Authorization: `Bearer ${token}` } });
      retryCountRef.current = 0;
      setSaveStatus('saved');
    } catch {
      setSaveStatus('error');
      if (retryCountRef.current < 2) {
        retryCountRef.current++;
        retryTimerRef.current = setTimeout(() => performAutosave(true), 3000);
      }
    }
  };

  const scheduleSkillsAutosave = () => {
    if (isOnboarding) return;
    if (skillAutosaveTimerRef.current) clearTimeout(skillAutosaveTimerRef.current);
    setSaveStatus('pending');
    skillAutosaveTimerRef.current = setTimeout(async () => {
      setSaveStatus('saving');
      try {
        const currentNames = (formDataRef.current.skills || []).map(s => s.name);
        const toAdd = pendingSkillNamesRef.current.filter(n => !currentNames.includes(n));
        const toRemove = (formDataRef.current.skills || []).filter(s => !pendingSkillNamesRef.current.includes(s.name));
        await Promise.all([
          ...toAdd.map(name => axios.post(`${API_URL}/api/students/profile/skills`, { skill_name: name }, { headers: { Authorization: `Bearer ${token}` } })),
          ...toRemove.map(s => axios.delete(`${API_URL}/api/students/profile/skills/${s._id}`, { headers: { Authorization: `Bearer ${token}` } })),
        ]);
        setSaveStatus('saved');
        fetchProfile();
      } catch { setSaveStatus('error'); }
    }, 2500);
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    scheduleAutosave();
  };

  // Onboarding save + advance / finish
  const handleSaveStepAndContinue = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/students/profile`, formDataRef.current, { headers: { Authorization: `Bearer ${token}` } });
      fetchProfile();
      setOnboardingStep(s => s + 1);
    } catch (err) {
      showToast('Error: ' + (err.response?.data?.error || 'Update failed'), 'error');
    } finally { setSaving(false); }
  };

  const handleFinishOnboarding = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/students/profile`, formDataRef.current, { headers: { Authorization: `Bearer ${token}` } });
      localStorage.setItem(`ob_${student._id}`, '1');
      fetchProfile();
      setActiveTab('dashboard');
    } catch (err) {
      showToast('Error: ' + (err.response?.data?.error || 'Update failed'), 'error');
    } finally { setSaving(false); }
  };


  const handleTogglePendingSkill = (skillName) => {
    setPendingSkillNames(prev => {
      if (prev.includes(skillName)) return prev.filter(s => s !== skillName);
      if (prev.length >= 10) return prev;
      return [...prev, skillName];
    });
    scheduleSkillsAutosave();
  };

  const handleSaveSkills = async () => {
    setSavingSkills(true);
    try {
      const currentNames = (formData.skills || []).map(s => s.name);
      const toAdd = pendingSkillNames.filter(n => !currentNames.includes(n));
      const toRemove = (formData.skills || []).filter(s => !pendingSkillNames.includes(s.name));
      await Promise.all([
        ...toAdd.map(name => axios.post(`${API_URL}/api/students/profile/skills`, { skill_name: name }, { headers: { Authorization: `Bearer ${token}` } })),
        ...toRemove.map(s => axios.delete(`${API_URL}/api/students/profile/skills/${s._id}`, { headers: { Authorization: `Bearer ${token}` } })),
      ]);
      showToast('Skills saved');
      fetchProfile();
    } catch { showToast('Error saving skills', 'error'); }
    finally { setSavingSkills(false); }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') { showToast('Only PDF files allowed', 'error'); return; }
    if (file.size > 5 * 1024 * 1024) { showToast('File size must be under 5MB', 'error'); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/api/students/upload/resume`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      showToast('Resume uploaded successfully');
      setFormData(prev => ({ ...prev, resume_url: res.data.resume_url }));
      fetchProfile();
    } catch (err) {
      showToast('Upload failed: ' + (err.response?.data?.error || 'Unknown error'), 'error');
    } finally { setUploading(false); }
  };

  const handleDeleteResume = async () => {
    if (!window.confirm('Delete your current resume? This cannot be undone.')) return;
    setDeletingResume(true);
    try {
      await axios.delete(`${API_URL}/api/students/upload/resume`, { headers: { Authorization: `Bearer ${token}` } });
      showToast('Resume deleted');
      setFormData(prev => ({ ...prev, resume_url: null }));
      fetchProfile();
    } catch { showToast('Failed to delete resume', 'error'); }
    finally { setDeletingResume(false); }
  };

  const handleRequestVerification = async () => {
    setRequestingVerification(true);
    try {
      await axios.post(`${API_URL}/api/students/request-verification`, {}, { headers: { Authorization: `Bearer ${token}` } });
      showToast('Verification request submitted! Admin will review your profile.');
      fetchProfile();
    } catch (err) {
      showToast(err.response?.data?.error || 'Request failed', 'error');
    } finally { setRequestingVerification(false); }
  };

  const handleLogout = () => { onLogout(); navigate('/'); };

  // ── Loading state ─────────────────────────────────────────────────────────

  if (!student) {
    return <div className="sd-page"><div className="loading"><div className="spinner" /> Loading…</div></div>;
  }

  // ── Derived display values ─────────────────────────────────────────────────

  const userName = student.name || 'Student';
  const userInitial = userName.charAt(0).toUpperCase();
  const noticePeriod = formData.notice_period || '';
  const workModeArray = Array.isArray(formData.work_mode) ? formData.work_mode : [];
  const rateMin = formData.monthly_rate_min || 0;
  const rateMax = formData.monthly_rate_max || 50000;
  const skillsCount = (formData.skills || []).length;

  const POPULAR_SKILLS = [
    'Python','JavaScript','React','Node.js','Machine Learning','Data Analysis','UI/UX Design','Figma',
    'Product Management','Video Editing','Digital Marketing','Java','C++','TypeScript','Next.js','Django',
    'AWS','Docker','Flutter','Financial Modeling','SEO','Graphic Design','Content Writing','Project Management','Canva',
  ];
  const filteredSkills = skillSearch.trim()
    ? availableSkills.filter(s => s.name.toLowerCase().includes(skillSearch.toLowerCase()))
    : availableSkills.filter(s => POPULAR_SKILLS.includes(s.name));

  // Missing fields checklist
  const missingFields = [
    { done: !!formData.bio,           label: 'Bio / About You',                  action: () => setActiveTab('profile') },
    { done: !!formData.phone,         label: 'Phone Number',                     action: () => setActiveTab('profile') },
    { done: !!formData.resume_url,    label: 'Resume (PDF)',                     action: () => setActiveTab('profile') },
    { done: skillsCount > 0,          label: 'At least one skill',               action: () => setActiveTab('profile') },
    { done: !!noticePeriod,           label: 'Notice period',                    action: () => setActiveTab('profile') },
    { done: workModeArray.length > 0, label: 'Work mode preference',             action: () => setActiveTab('profile') },
    { done: !!formData.weekly_commitment, label: 'Weekly availability (hrs/wk)', action: () => setActiveTab('profile') },
    { done: !!formData.monthly_rate_min,  label: 'Expected stipend range',       action: () => setActiveTab('profile') },
    { done: !!(formData.linkedin_url || formData.github_url), label: 'LinkedIn or GitHub link', action: () => setActiveTab('profile') },
  ];
  const incomplete = missingFields.filter(f => !f.done);

  // Shared job card props factory
  const jobCardProps = (job) => ({
    job, student,
    onApply: handleApply,
    onSave: handleSaveJob,
    onUnsave: handleUnsaveJob,
    isSaved: isJobSaved(job._id),
    appliedStatus: getApplicationStatus(job._id),
    applying: applyingTo === job._id,
    onViewDetails: setSelectedJob,
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div className="sd-page">

      {/* ── Navbar ── */}
      <nav className="sd-nav">
        <span className="sd-nav-brand"><img src={logoMark} alt="" className="brand-mark" />HYRSKILL</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={() => navigate('/student/resume-score')} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', background: 'none', border: '1px solid var(--border)', borderRadius: 8, padding: '6px 12px', cursor: 'pointer' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20V10"/><path d="M18 20V4"/><path d="M6 20v-4"/></svg>
            <span className="sd-nav-msg-label">Resume Score</span>
          </button>
          <button onClick={() => { setUnreadMsgCount(0); navigate('/messages'); }} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', background: 'none', border: '1px solid var(--border)', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', position: 'relative' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
            <span className="sd-nav-msg-label">Messages</span>
            {/* Keyed on the count so a newly arrived message scales the badge
                in again rather than silently changing a digit. */}
            {unreadMsgCount > 0 && (
              <span key={unreadMsgCount} className="ui-badge-pop" style={{ position: 'absolute', top: -6, right: -6, background: 'var(--danger)', color: '#fff', borderRadius: '50%', fontSize: 10, fontWeight: 700, minWidth: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px', lineHeight: 1 }}>
                {unreadMsgCount > 99 ? '99+' : unreadMsgCount}
              </span>
            )}
          </button>
          <div className="sd-profile-wrap" ref={dropdownRef}>
            <button className="sd-profile-btn" onClick={() => setShowProfileDropdown(v => !v)}>{userInitial}</button>
            {showProfileDropdown && (
              <div className="sd-profile-dropdown">
                <div className="sd-dropdown-header">
                  <div className="sd-dropdown-name">{student.name}</div>
                  <div className="sd-dropdown-email">{student.email}</div>
                </div>
                <button className="sd-dropdown-item" onClick={() => { setActiveTab('profile'); setShowProfileDropdown(false); }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  Edit Profile
                </button>
                <button className="sd-dropdown-item danger" onClick={handleLogout}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* ── Tab bar — the active underline travels between tabs ── */}
      <TabNav
        className="sd-tabs-bar"
        tabClassName="sd-tab-btn"
        ariaLabel="Dashboard sections"
        active={activeTab}
        onChange={setActiveTab}
        items={[
          { id: 'dashboard', label: 'Dashboard', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg> },
          { id: 'opportunities', label: 'Opportunities', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-4 0v2"/></svg> },
          { id: 'applications', label: 'Applications', badge: applications.length, icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg> },
          { id: 'assignments', label: 'Assignments', badge: assignments.filter(a => !a.submission).length, badgeTone: 'danger', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg> },
          { id: 'profile', label: 'Profile', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg> },
        ]}
      />

      {/* ── Toast ── */}
      {toast && (
        <div style={{ position: 'fixed', top: 76, right: 24, zIndex: 1100 }}>
          <div className={`alert alert-${toast.type} ui-toast`} style={{ minWidth: 280, boxShadow: 'var(--ui-shadow-pop)' }}>{toast.msg}</div>
        </div>
      )}

      {/* ── Resume unreachable modal ── */}
      {/* Not dismissible by overlay click: the student has to acknowledge that
          the application did NOT go through. */}
      {resumeBlock && (
        <div className="pcm-overlay">
          <div className="pcm-card" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="pcm-top">
              <div className="pcm-emoji">📄</div>
              <h2>Application Not Submitted</h2>
              <p>{resumeBlock.message}</p>
            </div>
            <div className="pcm-body">
              <div className="alert alert-error" style={{ marginBottom: 16, fontSize: 13 }}>
                Recruiters open your resume straight from your profile. Until it loads, your
                application can&apos;t be sent.
              </div>
              <button className="btn btn-full" onClick={goToResumeUpload}>Re-upload Resume</button>
              <button
                className="btn btn-secondary btn-full"
                style={{ marginTop: 8 }}
                onClick={() => setResumeBlock(null)}
              >
                Not Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Completion modal ── */}
      {showCompletionModal && (
        <div className="pcm-overlay" onClick={() => setShowCompletionModal(false)}>
          <div className="pcm-card" onClick={e => e.stopPropagation()}>
            <div className="pcm-top">
              <div className="pcm-emoji">🎉</div>
              <h2>Profile Looking Great!</h2>
              <p>Your profile is strong enough to attract recruiters. Keep it updated to stay at the top.</p>
            </div>
            <div className="pcm-body">
              <div className="pcm-stats">
                <div className="pcm-stat"><div className="pcm-stat-val">{pct}%</div><div className="pcm-stat-label">Complete</div></div>
                <div className="pcm-stat"><div className="pcm-stat-val">{skillsCount}</div><div className="pcm-stat-label">Skills</div></div>
                <div className="pcm-stat"><div className="pcm-stat-val">{noticePeriod || '—'}</div><div className="pcm-stat-label">Notice Period</div></div>
              </div>
              <button className="btn btn-full" onClick={() => setShowCompletionModal(false)}>View Dashboard</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Viewers paywall modal ── */}
      {showViewersPaywall && (
        <div className="pcm-overlay" onClick={() => setShowViewersPaywall(false)}>
          <div className="pcm-card" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="pcm-top">
              <div className="pcm-emoji">👀</div>
              <h2>See Who Viewed Your Profile</h2>
              <p>{profileViewCount} recruiter{profileViewCount !== 1 ? 's' : ''} have viewed your profile. Unlock their identities and reach out on LinkedIn.</p>
            </div>
            <div className="pcm-body">
              <div style={{ background: 'var(--bg)', borderRadius: 12, padding: '16px 20px', marginBottom: 16, border: '1px solid var(--border)' }}>
                <div style={{ fontWeight: 700, fontSize: 22, marginBottom: 2 }}>₹99 <span style={{ fontWeight: 400, fontSize: 14, color: 'var(--text-muted)' }}>one-time</span></div>
                <ul style={{ listStyle: 'none', padding: 0, margin: '10px 0 0', fontSize: 13, color: 'var(--text-secondary)', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <li>✓ See recruiter name &amp; company</li><li>✓ Get their LinkedIn to message directly</li><li>✓ View date they visited your profile</li>
                </ul>
              </div>
              <div style={{ textAlign: 'center', marginBottom: 12 }}>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>Scan to pay via UPI</div>
                <img src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent('upi://pay?pa=admin@iittalent&pn=IIT%20Talent&am=99&cu=INR&tn=ProfileViewers')}`} alt="UPI QR" style={{ borderRadius: 8, border: '1px solid var(--border)' }} />
                <div style={{ fontSize: 13, fontWeight: 600, marginTop: 6 }}>₹99 · admin@iittalent</div>
              </div>
              <button className="btn btn-full" onClick={() => { localStorage.setItem(`viewers_unlocked_${student?._id || ''}`, '1'); setViewersUnlocked(true); setShowViewersPaywall(false); }}>I've Completed Payment — Unlock</button>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', marginTop: 8 }}>Access unlocks instantly after confirmation</p>
            </div>
          </div>
        </div>
      )}

      {/* ── Job detail modal ── */}
      {/* Rendered unconditionally so the dialog can play its exit before
          it leaves the tree; it returns null when nothing has been opened. */}
      <JobDetailModal
        job={selectedJob}
        student={student}
        onClose={() => setSelectedJob(null)}
        onApply={handleApply}
        onSave={handleSaveJob}
        onUnsave={handleUnsaveJob}
        isSaved={isJobSaved(selectedJob?._id)}
        appliedStatus={getApplicationStatus(selectedJob?._id)}
        applying={applyingTo === selectedJob?._id}
      />

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* DASHBOARD TAB                                                       */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {activeTab === 'dashboard' && (
        <div className="sd-body ui-view">

          {pct < 100 ? (
            /* ── INCOMPLETE: Profile completion view ── */
            <>
              {/* Welcome header — greeting, progress and the one next step */}
              <div className="dash-welcome">
                <div className="dash-welcome-avatar">{userInitial}</div>
                <div className="dash-welcome-text">
                  <h2>Hi, {student.name.split(' ')[0]}! <span aria-hidden="true">👋</span></h2>
                  <p>
                    Your profile is {pct}% complete.{' '}
                    {pct >= 80
                      ? 'You’re almost there.'
                      : 'Finish it to unlock opportunities and get discovered by recruiters.'}
                  </p>
                  <div className="dash-welcome-progress">
                    <AnimatedProgress value={pct} height={6} onDark delay={220} />
                    <span className="dash-welcome-pct">{pct}%</span>
                  </div>
                </div>
                <div className="dash-welcome-side">
                  <span className={`dash-welcome-badge${student.screening_status === 'Verified' ? ' verified' : ''}`}>{student.screening_status}</span>
                  <button type="button" className="dash-welcome-cta ui-press" onClick={() => setActiveTab('profile')}>
                    Complete Profile
                  </button>
                </div>
              </div>

              {/* Completion progress */}
              <div className="card" style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: 16 }}>Profile Completion</h3>
                    <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>{incomplete.length} item{incomplete.length !== 1 ? 's' : ''} remaining to unlock the full dashboard</p>
                  </div>
                  <div style={{ fontSize: 32, fontWeight: 700, color: 'var(--primary)' }}>{pct}%</div>
                </div>
                <AnimatedProgress value={pct} height={10} className="sd-completion-bar" />

                {/* Missing sections */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
                  {missingFields.map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 10, background: f.done ? '#F0FDF4' : '#FFFBEB', border: `1px solid ${f.done ? '#BBF7D0' : '#FDE68A'}` }}>
                      <span style={{ fontSize: 16, flexShrink: 0 }}>{f.done ? '✅' : '○'}</span>
                      <span style={{ fontSize: 13, fontWeight: 500, flex: 1, color: f.done ? '#166534' : '#92400E', textDecoration: f.done ? 'line-through' : 'none', opacity: f.done ? 0.7 : 1 }}>{f.label}</span>
                      {!f.done && (
                        <button onClick={f.action} style={{ background: 'none', border: 'none', color: 'var(--primary)', fontWeight: 700, fontSize: 12, cursor: 'pointer', padding: '2px 0', flexShrink: 0 }}>Add →</button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Secondary on purpose: the banner above already carries the
                    primary call to action, and two of them compete. */}
                <button className="btn btn-secondary btn-full" style={{ marginTop: 20 }} onClick={() => setActiveTab('profile')}>Go to Profile</button>
              </div>

              {/* Verification card */}
              {(() => {
                const status = student.screening_status;
                if (status === 'Verified') return null;
                return (
                  <div style={{ marginBottom: 24, background: status === 'Pending' ? '#EFF6FF' : '#FFFBEB', borderRadius: 14, padding: '20px 24px', border: `1px solid ${status === 'Pending' ? '#BFDBFE' : '#FDE68A'}` }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                      <div style={{ fontSize: 22 }}>{status === 'Pending' ? '⏳' : status === 'Rejected' ? '❌' : '📋'}</div>
                      <div>
                        {status === 'Pending' && <><div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Verification Under Review</div><div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Your profile is being reviewed (1–2 business days).</div></>}
                        {status === 'Rejected' && <><div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Verification Rejected</div><div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 10 }}>Update your profile and re-apply.</div><button className="btn btn-sm" onClick={() => setActiveTab('profile')}>Update Profile</button></>}
                        {status === 'Under Review' && <><div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Complete your profile to apply for verification</div><div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Fill in the missing details above to become searchable by recruiters.</div></>}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Available opportunities — always visible */}
              <FeedSection
                title="Available Opportunities"
                subtitle="Browse open roles. Complete your profile and get verified to apply."
                actionLabel="See all →"
                onAction={() => setActiveTab('opportunities')}
                loading={jobsLoading}
              >
                {jobs.length === 0
                  ? <EmptyJobsState onInvite={() => window.open('mailto:?subject=Join%20HyrSkill%20as%20a%20Recruiter', '_blank')} />
                  : <Stagger className="sd-card-grid">
                      {[...jobs].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 4).map(job => (
                        <JobCard key={job._id} {...jobCardProps(job)} showMatch={false} />
                      ))}
                    </Stagger>
                }
              </FeedSection>
            </>
          ) : (
            /* ── COMPLETE: Opportunities feed ── */
            <>
              {/* Welcome header */}
              <div className="dash-welcome">
                <div className="dash-welcome-avatar">{userInitial}</div>
                <div className="dash-welcome-text">
                  <h2>Hi, {student.name.split(' ')[0]}! <span aria-hidden="true">👋</span></h2>
                  <p>
                    Your profile is complete{student.screening_status === 'Verified' ? ' and verified' : ''}.
                    {' '}{recommendedJobs.length > 0
                      ? `${recommendedJobs.length} role${recommendedJobs.length === 1 ? '' : 's'} match you right now.`
                      : 'Start applying to opportunities.'}
                  </p>
                </div>
                <div className="dash-welcome-side">
                  <span className={`dash-welcome-badge${student.screening_status === 'Verified' ? ' verified' : ''}`}>{student.screening_status}</span>
                  <button type="button" className="dash-welcome-cta ui-press" onClick={() => setActiveTab('opportunities')}>
                    Browse Opportunities
                  </button>
                </div>
              </div>

              {/* Verification notice (if not yet verified) */}
              {student.screening_status !== 'Verified' && student.screening_status !== 'Pending' && (
                <div style={{ marginBottom: 20, background: '#F0FDF4', borderRadius: 12, padding: '14px 20px', border: '1px solid #BBF7D0', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 20 }}>✅</span>
                  <div style={{ flex: 1, fontSize: 14, color: '#15803D' }}>
                    <strong>Profile complete!</strong> Apply for verification to become discoverable by recruiters.
                  </div>
                  <button className="btn btn-sm" onClick={handleRequestVerification} disabled={requestingVerification}>{requestingVerification ? 'Submitting…' : 'Apply for Verification'}</button>
                </div>
              )}
              {student.screening_status === 'Pending' && (
                <div style={{ marginBottom: 20, background: '#EFF6FF', borderRadius: 12, padding: '14px 20px', border: '1px solid #BFDBFE', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 20 }}>⏳</span>
                  <div style={{ fontSize: 14, color: '#1E40AF' }}>Verification is under review. You will be notified once approved (1–2 business days).</div>
                </div>
              )}

              {/* Recommended Opportunities */}
              <FeedSection
                title="Recommended For You"
                subtitle="Matched based on your skills, branch and graduation year."
                actionLabel="See all →"
                onAction={() => { setOppSubTab('recommended'); setActiveTab('opportunities'); }}
                loading={jobsLoading}
              >
                {jobs.length === 0
                  ? <EmptyJobsState onInvite={() => window.open('mailto:?subject=Join%20HyrSkill%20as%20a%20Recruiter', '_blank')} />
                  : <Stagger className="sd-card-grid">
                      {recommendedJobs.map(job => <JobCard key={job._id} {...jobCardProps(job)} />)}
                    </Stagger>
                }
              </FeedSection>

              {/* Where the job search actually stands, in four numbers. */}
              <Reveal as="section" className="sd-feed">
                <div className="sd-feed__head">
                  <h3>Your Progress</h3>
                  <button type="button" className="sd-feed__action" onClick={() => setActiveTab('applications')}>
                    View applications →
                  </button>
                </div>
                <p className="sd-feed__sub">How far each application has travelled.</p>
                <div className="sd-metrics">
                  {APPLICATION_SUMMARY.map(({ key, label, hint, tone, icon }) => (
                    <MetricTile
                      key={key}
                      value={applicationSummary[key]}
                      label={label}
                      hint={hint}
                      tone={tone}
                      icon={icon}
                      onClick={() => setActiveTab('applications')}
                    />
                  ))}
                </div>
              </Reveal>

              {/* My Applications */}
              <FeedSection
                title="My Applications"
                actionLabel="View all →"
                onAction={() => setActiveTab('applications')}
              >
                {applications.length === 0 ? (
                  <EmptyState
                    art="document"
                    title="No applications yet"
                    body="Pick a role that matches you and hit Apply Now — recruiters see your profile the moment you do."
                    action={() => setActiveTab('opportunities')}
                    actionLabel="Browse opportunities"
                    style={{ padding: '28px 24px' }}
                  />
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {applications.slice(0, 3).map(app => {
                      const job = typeof app.job_id === 'object' ? app.job_id : null;
                      const company = companyOf(job);
                      const style = APPLICATION_STATUS_STYLE[app.status] || APPLICATION_STATUS_STYLE['Applied'];
                      return (
                        <div key={app._id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', border: '1px solid var(--border)', borderRadius: 12, background: 'var(--surface)' }}>
                          <div style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: 'var(--primary)', fontSize: 14, flexShrink: 0 }}>{company[0]?.toUpperCase()}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: 14 }}>{job?.title || 'Position'}</div>
                            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{company} · {new Date(app.applied_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} · {applicationRef(app)}</div>
                          </div>
                          <span style={{ ...style, fontSize: 11, padding: '4px 10px', borderRadius: 20, fontWeight: 600, flexShrink: 0 }}>{app.status}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </FeedSection>

              {/* Latest Opportunities */}
              <FeedSection
                title="Latest Opportunities"
                actionLabel="View all →"
                onAction={() => { setOppSubTab('newest'); setActiveTab('opportunities'); }}
                loading={jobsLoading}
              >
                {jobs.length === 0
                  ? <EmptyJobsState onInvite={() => window.open('mailto:?subject=Join%20HyrSkill%20as%20a%20Recruiter', '_blank')} />
                  : <Stagger className="sd-card-grid">
                      {[...jobs].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 4).map(job => (
                        <JobCard key={job._id} {...jobCardProps(job)} showMatch={false} />
                      ))}
                    </Stagger>
                }
              </FeedSection>

              {/* Saved Jobs */}
              <FeedSection title="Saved Jobs" actionLabel={savedJobObjects.length > 3 ? 'View all →' : undefined} onAction={savedJobObjects.length > 3 ? () => setActiveTab('opportunities') : undefined}>
                {savedJobObjects.length === 0 ? (
                  <EmptyState
                    art="bookmark"
                    title="Nothing saved yet"
                    body="Use the bookmark on any opportunity to keep it here while you decide."
                    action={() => setActiveTab('opportunities')}
                    actionLabel="Browse opportunities"
                    style={{ padding: '28px 24px' }}
                  />
                ) : (
                  <Stagger className="sd-card-grid">
                    {savedJobObjects.slice(0, 4).map(job => <JobCard key={job._id} {...jobCardProps(job)} />)}
                  </Stagger>
                )}
              </FeedSection>

              {/* Profile Summary */}
              <div className="card">
                <div className="section-header">
                  <h2>Profile Summary</h2>
                  <button className="btn btn-secondary btn-sm" onClick={() => setActiveTab('profile')}>Edit Profile</button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
                  <div style={{ padding: '12px 16px', background: 'var(--bg)', borderRadius: 10, border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>Profile Views</div>
                    <div style={{ fontSize: 22, fontWeight: 700 }}>{profileViewCount}</div>
                  </div>
                  <div style={{ padding: '12px 16px', background: 'var(--bg)', borderRadius: 10, border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>Skills</div>
                    <div style={{ fontSize: 22, fontWeight: 700 }}>{skillsCount}</div>
                  </div>
                  <div style={{ padding: '12px 16px', background: 'var(--bg)', borderRadius: 10, border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>Notice Period</div>
                    <div style={{ fontSize: 16, fontWeight: 700 }}>{noticePeriod || '—'}</div>
                  </div>
                  <div style={{ padding: '12px 16px', background: 'var(--bg)', borderRadius: 10, border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>Status</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: student.screening_status === 'Verified' ? '#15803D' : 'var(--text-primary)' }}>{student.screening_status}</div>
                  </div>
                </div>
                {formData.skills?.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {formData.skills.map(s => <span key={s._id} className="skill-tag">{s.name}</span>)}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* OPPORTUNITIES TAB                                                   */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {activeTab === 'opportunities' && (
        <div className="sd-body ui-view">
          <div style={{ marginBottom: 20 }}>
            <h2 style={{ margin: '0 0 4px', fontSize: 20 }}>Opportunities</h2>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--text-muted)' }}>Browse all recruiter postings. Apply directly or save for later.</p>
          </div>

          {/* Filters */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20, padding: '16px 20px', background: 'var(--surface)', borderRadius: 14, border: '1px solid var(--border)' }}>
            <select value={filterType} onChange={e => setFilterType(e.target.value)}
              style={{ padding: '8px 12px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 13, fontFamily: 'var(--font)', background: filterType ? 'var(--primary-light)' : 'var(--surface)', color: filterType ? 'var(--primary)' : 'var(--text-primary)', cursor: 'pointer' }}>
              <option value="">All Types</option>
              <option>Internship</option>
              <option>Full-time</option>
              <option>Part-time</option>
              <option>Contract</option>
            </select>
            <select value={filterMode} onChange={e => setFilterMode(e.target.value)}
              style={{ padding: '8px 12px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 13, fontFamily: 'var(--font)', background: filterMode ? 'var(--primary-light)' : 'var(--surface)', color: filterMode ? 'var(--primary)' : 'var(--text-primary)', cursor: 'pointer' }}>
              <option value="">All Locations</option>
              <option>Remote</option>
              <option>Hybrid</option>
              <option>On-site</option>
            </select>
            {(filterType || filterMode) && (
              <button onClick={() => { setFilterType(''); setFilterMode(''); }} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', cursor: 'pointer', fontSize: 13, color: 'var(--text-muted)' }}>✕ Clear filters</button>
            )}
            <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--text-muted)', alignSelf: 'center' }}>{getFilteredJobs().length} result{getFilteredJobs().length !== 1 ? 's' : ''}</span>
          </div>

          {/* Sub-tabs */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '2px solid var(--border)', paddingBottom: 0 }}>
            {[['all', 'All Opportunities'], ['recommended', 'Recommended'], ['newest', 'Newest First']].map(([id, label]) => (
              <button key={id} onClick={() => setOppSubTab(id)}
                style={{ padding: '8px 16px', border: 'none', background: 'none', fontSize: 13, fontWeight: oppSubTab === id ? 700 : 500, cursor: 'pointer', color: oppSubTab === id ? 'var(--primary)' : 'var(--text-muted)', borderBottom: `2px solid ${oppSubTab === id ? 'var(--primary)' : 'transparent'}`, marginBottom: -2 }}>
                {label}
              </button>
            ))}
          </div>

          {/* Job grid */}
          {jobsLoading ? (
            <SkeletonGrid count={6} />
          ) : getFilteredJobs().length === 0 ? (
            (filterType || filterMode)
              ? <EmptyState
                  art="search"
                  title="No roles match these filters"
                  body="Try widening the type or location filter — new roles are posted every week."
                  action={() => { setFilterType(''); setFilterMode(''); }}
                  actionLabel="Clear filters"
                />
              : <EmptyJobsState onInvite={() => window.open('mailto:?subject=Join%20HyrSkill%20as%20a%20Recruiter', '_blank')} />
          ) : (
            <Stagger className="sd-card-grid">
              {getFilteredJobs().map(job => (
                <JobCard key={job._id} {...jobCardProps(job)} showMatch={oppSubTab === 'recommended'} />
              ))}
            </Stagger>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* APPLICATIONS TAB                                                    */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {activeTab === 'applications' && (
        <div className="sd-body ui-view">
          <div style={{ marginBottom: 20 }}>
            <h2 style={{ margin: '0 0 4px', fontSize: 20 }}>My Applications</h2>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--text-muted)' }}>
              Track every application you&apos;ve submitted through HyrSkill. Quote the application ID when you follow up.
            </p>
          </div>

          {applications.length === 0 ? (
            <EmptyState
              art="document"
              title="No applications yet"
              body="Find an opportunity and hit Apply Now. Every application you send shows up here with its live status."
              action={() => setActiveTab('opportunities')}
              actionLabel="Browse Opportunities"
              style={{ padding: '64px 24px' }}
            />
          ) : (
            /* One card component, shared with the receipt the job page shows
               straight after an application is submitted, so the two can never
               describe the same application differently. */
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {applications.map(app => (
                <ApplicationCard key={app._id} application={app} onViewJob={setSelectedJob} />
              ))}
            </div>
          )}

        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* ASSIGNMENTS TAB                                                      */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {activeTab === 'assignments' && (
        <div className="sd-body ui-view">
          <div style={{ marginBottom: 20 }}>
            <h2 style={{ margin: '0 0 4px', fontSize: 20 }}>My Assignments</h2>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--text-muted)' }}>Tasks posted by recruiters for the jobs you've applied to.</p>
          </div>

          {assignmentsLoading && (
            <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-muted)' }}>
              <div className="rd-big-spin" style={{ margin: '0 auto 12px' }} />
              <p style={{ fontSize: 14 }}>Loading assignments…</p>
            </div>
          )}

          {!assignmentsLoading && assignments.length === 0 && (
            <div style={{ textAlign: 'center', padding: '64px 24px' }}>
              <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1" style={{ margin: '0 auto 16px', display: 'block', opacity: 0.4 }}>
                <path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
              </svg>
              <p style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>No assignments yet</p>
              <p style={{ fontSize: 14, color: 'var(--text-muted)' }}>When a recruiter posts an assignment for a job you applied to, it will appear here.</p>
            </div>
          )}

          {!assignmentsLoading && assignments.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {assignments.map(a => {
                const job = a.job_id;
                const isSubmitted = !!a.submission;
                const isOverdue = a.due_date && new Date(a.due_date) < new Date() && !isSubmitted;
                const dueDate = a.due_date
                  ? new Date(a.due_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                  : null;
                return (
                  <div key={a._id} style={{ border: `1px solid ${isOverdue ? '#FECACA' : 'var(--border)'}`, borderRadius: 14, padding: '18px 20px', background: isOverdue ? '#FFF5F5' : 'var(--surface)' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 2 }}>{a.title}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                          {job?.title && <span>{job.title}</span>}
                          {job?.company_name && <span> · {job.company_name}</span>}
                        </div>
                      </div>
                      <span style={{
                        fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, flexShrink: 0,
                        background: isSubmitted ? '#D1FAE5' : isOverdue ? '#FEE2E2' : '#DBEAFE',
                        color: isSubmitted ? '#065F46' : isOverdue ? '#DC2626' : '#1D4ED8',
                      }}>
                        {isSubmitted ? (a.submission.status === 'reviewed' ? 'Reviewed' : 'Submitted') : isOverdue ? 'Overdue' : 'Pending'}
                      </span>
                    </div>

                    <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                      {a.description.length > 200 && !expandedAssignments[a._id]
                        ? a.description.slice(0, 197) + '…'
                        : a.description}
                      {a.description.length > 200 && (
                        <button onClick={() => setExpandedAssignments(prev => ({ ...prev, [a._id]: !prev[a._id] }))}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', fontSize: 12, fontWeight: 700, padding: '0 0 0 4px' }}>
                          {expandedAssignments[a._id] ? 'Show less' : 'Read more'}
                        </button>
                      )}
                    </p>

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 12, color: 'var(--text-muted)' }}>
                        {dueDate && <span style={{ color: isOverdue ? '#DC2626' : 'var(--text-muted)', fontWeight: isOverdue ? 700 : 400 }}>Due: {dueDate}</span>}
                        {isSubmitted && (
                          <span style={{ color: '#059669' }}>
                            Submitted {new Date(a.submission.submitted_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                          </span>
                        )}
                      </div>
                      {!isSubmitted && (
                        <button onClick={() => { setSubmitModal({ open: true, assignment: a }); setSubmitForm({ link_url: '', comments: '' }); setSubmitFile(null); }}
                          style={{ background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 9, padding: '8px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                          Submit Work
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Submit assignment modal ── */}
      {submitModal.open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={() => setSubmitModal({ open: false, assignment: null })}>
          <div style={{ background: '#fff', borderRadius: 18, padding: 28, width: '100%', maxWidth: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 18 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 2 }}>Submit Assignment</div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{submitModal.assignment?.title}</div>
              </div>
              <button onClick={() => setSubmitModal({ open: false, assignment: null })} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text-muted)', lineHeight: 1 }}>×</button>
            </div>
            <form onSubmit={handleSubmitAssignment}>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Upload File (PDF, ZIP, etc.)</label>
                <input type="file" onChange={e => setSubmitFile(e.target.files[0])}
                  style={{ fontSize: 13, width: '100%' }} />
              </div>
              <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-muted)', margin: '8px 0' }}>— or —</div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Link (GitHub, Google Drive, etc.)</label>
                <input type="url" value={submitForm.link_url} onChange={e => setSubmitForm(f => ({ ...f, link_url: e.target.value }))}
                  placeholder="https://github.com/…"
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 9, border: '1.5px solid var(--border)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: 18 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Comments (optional)</label>
                <textarea value={submitForm.comments} onChange={e => setSubmitForm(f => ({ ...f, comments: e.target.value }))}
                  placeholder="Any notes for the recruiter…" rows={3} maxLength={1000}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 9, border: '1.5px solid var(--border)', fontSize: 13, outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
              </div>
              <button type="submit" disabled={submitting || (!submitFile && !submitForm.link_url)}
                style={{ width: '100%', background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 0', fontSize: 14, fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1 }}>
                {submitting ? 'Submitting…' : 'Submit Assignment'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* PROFILE TAB                                                         */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {activeTab === 'profile' && (
        <div className="sd-body prof-tab-body ui-view">
          {isOnboarding ? (

            /* ── ONBOARDING WIZARD ── */
            <div className="ob-wizard">
              {/* Step progress */}
              <div className="ob-header">
                <div className="ob-steps">
                  {['Basic Info', 'Availability', 'Resume', 'Skills', 'Links'].map((label, i) => (
                    <React.Fragment key={i}>
                      <div className={`ob-step-item${i === onboardingStep ? ' active' : ''}${i < onboardingStep ? ' done' : ''}`}>
                        <div className="ob-step-dot">
                          {i < onboardingStep
                            ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                            : i + 1}
                        </div>
                        <span className="ob-step-label">{label}</span>
                      </div>
                      {i < 4 && <div className={`ob-step-connector${i < onboardingStep ? ' done' : ''}`} />}
                    </React.Fragment>
                  ))}
                </div>
                <div className="ob-step-counter">Step {onboardingStep + 1} of 5</div>
              </div>

              {/* Step 0: Basic Info */}
              {onboardingStep === 0 && (
                <div className="prof-section">
                  <div className="prof-section-header">
                    <div className="prof-section-icon">👤</div>
                    <div><h3>Basic Information</h3><p className="prof-section-desc">Tell recruiters who you are</p></div>
                  </div>
                  <form onSubmit={handleSaveStepAndContinue}>
                    <div className="prof-field-grid">
                      <div className="form-group prof-field">
                        <label>Full name</label>
                        <input type="text" name="name" value={formData.name || ''} onChange={handleChange} placeholder="Your full name" />
                      </div>
                      <div className="form-group prof-field">
                        <label>Phone number</label>
                        <input type="tel" name="phone" value={formData.phone || ''} onChange={handleChange} placeholder="+91 98765 43210" />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>About you <span className="prof-optional">optional</span></label>
                      <textarea name="bio" value={formData.bio || ''} onChange={handleChange} rows={4}
                        placeholder="Short intro about your background, interests, and the opportunities you're looking for…" />
                    </div>
                    <div className="prof-actions">
                      <button type="submit" className="btn" disabled={saving}>
                        {saving ? <><span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Saving…</> : 'Save & Continue →'}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Step 1: Availability */}
              {onboardingStep === 1 && (
                <div className="prof-section">
                  <div className="prof-section-header">
                    <div className="prof-section-icon">⏱</div>
                    <div><h3>Availability &amp; Stipend</h3><p className="prof-section-desc">Help recruiters understand when and how you can work</p></div>
                  </div>
                  <div className="form-group">
                    <label>Hours available per week</label>
                    <input type="number" name="weekly_commitment" value={formData.weekly_commitment || ''} onChange={handleChange} placeholder="e.g. 20" min={1} max={80} style={{ maxWidth: 180 }} />
                  </div>
                  <div className="form-group">
                    <label>When can you start?</label>
                    <div className="avail-grid">
                      {NOTICE_PERIOD_OPTIONS.map(opt => (
                        <label key={opt} className={`avail-option${noticePeriod === opt ? ' selected' : ''}`}>
                          <input type="radio" name="notice_period" checked={noticePeriod === opt} onChange={() => setFormData(prev => ({ ...prev, notice_period: opt }))} />
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ opacity: noticePeriod === opt ? 1 : 0.3, flexShrink: 0 }}><polyline points="20 6 9 17 4 12"/></svg>
                          {opt}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Work mode preference</label>
                    <div className="avail-grid">
                      {WORK_MODE_OPTIONS.map(opt => (
                        <label key={opt} className={`avail-option${workModeArray.includes(opt) ? ' selected' : ''}`}>
                          <input type="checkbox" checked={workModeArray.includes(opt)} onChange={() => {
                            setFormData(prev => {
                              const cur = Array.isArray(prev.work_mode) ? prev.work_mode : [];
                              return { ...prev, work_mode: cur.includes(opt) ? cur.filter(o => o !== opt) : [...cur, opt] };
                            });
                          }} />
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ opacity: workModeArray.includes(opt) ? 1 : 0.3, flexShrink: 0 }}><polyline points="20 6 9 17 4 12"/></svg>
                          {opt}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Expected monthly stipend</label>
                    <CompensationRange minVal={rateMin} maxVal={rateMax}
                      onMinChange={v => setFormData(prev => ({ ...prev, monthly_rate_min: v }))}
                      onMaxChange={v => setFormData(prev => ({ ...prev, monthly_rate_max: v }))} />
                  </div>
                  <div className="prof-actions">
                    <button className="btn" onClick={handleSaveStepAndContinue} disabled={saving}>
                      {saving ? <><span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Saving…</> : 'Save & Continue →'}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2: Resume */}
              {onboardingStep === 2 && (
                <div className="prof-section">
                  <div className="prof-section-header">
                    <div className="prof-section-icon">📄</div>
                    <div><h3>Resume</h3><p className="prof-section-desc">Upload your resume — it's the first thing recruiters see</p></div>
                  </div>
                  <div
                    className={`upload-zone${dragOver ? ' drag-over' : ''}`}
                    onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={e => { e.preventDefault(); setDragOver(false); handleFileUpload(e.dataTransfer.files[0]); }}
                    onClick={() => document.getElementById('resume-file-ob').click()}
                  >
                    <input id="resume-file-ob" type="file" accept=".pdf" onChange={e => handleFileUpload(e.target.files[0])} disabled={uploading} style={{ display: 'none' }} />
                    <div className="upload-zone-icon">
                      {uploading ? <span className="spinner" style={{ display: 'inline-block' }} /> : <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="1.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>}
                    </div>
                    <div className="upload-zone-title">{uploading ? 'Uploading…' : formData.resume_url ? 'Upload a new resume' : 'Click to upload or drag & drop'}</div>
                    <div className="upload-zone-sub">PDF only · max 5 MB</div>
                  </div>
                  {formData.resume_url && (
                    <div className="resume-current">
                      <div className="resume-current-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg></div>
                      <div className="resume-current-info"><div className="resume-current-name">Resume uploaded</div><div className="resume-current-sub">Your resume is live on your profile</div></div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <a href={`${API_URL}${formData.resume_url.replace('/uploads/', '/api/uploads/')}`} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm">View</a>
                        <button className="btn btn-danger btn-sm" onClick={handleDeleteResume} disabled={deletingResume}>{deletingResume ? '…' : 'Delete'}</button>
                      </div>
                    </div>
                  )}
                  <div className="prof-actions">
                    <button className="btn" onClick={() => setOnboardingStep(s => s + 1)}>
                      {formData.resume_url ? 'Continue →' : 'Skip for now →'}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Skills */}
              {onboardingStep === 3 && (
                <div className="prof-section">
                  <div className="prof-section-header">
                    <div className="prof-section-icon">🛠</div>
                    <div><h3>Skills</h3><p className="prof-section-desc">Select up to 10 skills — this powers recruiter matching</p></div>
                  </div>
                  <div className="selected-skills-box">
                    {pendingSkillNames.length === 0
                      ? <span style={{ color: 'var(--text-placeholder)', fontSize: 14 }}>Click skills below to add them</span>
                      : pendingSkillNames.map(name => (
                        <span key={name} className="selected-skill-tag">{name}<button onClick={() => handleTogglePendingSkill(name)}>×</button></span>
                      ))
                    }
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    <input type="text" placeholder="Search skills…" value={skillSearch} onChange={e => setSkillSearch(e.target.value)}
                      style={{ width: '100%', padding: '9px 14px', border: '1.5px solid var(--border)', borderRadius: 'var(--radius)', fontSize: 14, fontFamily: 'var(--font)' }} />
                  </div>
                  <div className="skill-pool">
                    {filteredSkills.length === 0
                      ? <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>No skills match "{skillSearch}"</span>
                      : filteredSkills.map(skill => {
                        const isSelected = pendingSkillNames.includes(skill.name);
                        const isDisabled = !isSelected && pendingSkillNames.length >= 10;
                        return (
                          <button key={skill._id} className={`skill-chip${isSelected ? ' selected' : ''}${isDisabled ? ' disabled' : ''}`}
                            onClick={() => !isDisabled && handleTogglePendingSkill(skill.name)} type="button">
                            {isSelected && <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                            {skill.name}
                          </button>
                        );
                      })
                    }
                  </div>
                  <div className="prof-actions">
                    <button className="btn" onClick={async () => { await handleSaveSkills(); setOnboardingStep(s => s + 1); }} disabled={savingSkills}>
                      {savingSkills ? <><span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Saving…</> : 'Save & Continue →'}
                    </button>
                    <button className="btn btn-secondary" onClick={() => setOnboardingStep(s => s + 1)}>Skip</button>
                  </div>
                </div>
              )}

              {/* Step 4: Profile Links */}
              {onboardingStep === 4 && (
                <div className="prof-section">
                  <div className="prof-section-header">
                    <div className="prof-section-icon">🔗</div>
                    <div><h3>Profile Links</h3><p className="prof-section-desc">Add your online presence to boost credibility</p></div>
                  </div>
                  <div className="form-group">
                    <label>LinkedIn URL <span className="prof-optional">optional</span></label>
                    <input type="url" name="linkedin_url" value={formData.linkedin_url || ''} onChange={handleChange} placeholder="https://linkedin.com/in/yourname" />
                  </div>
                  <div className="form-group">
                    <label>GitHub URL <span className="prof-optional">optional</span></label>
                    <input type="url" name="github_url" value={formData.github_url || ''} onChange={handleChange} placeholder="https://github.com/yourname" />
                  </div>
                  <div className="form-group">
                    <label>Portfolio URL <span className="prof-optional">optional</span></label>
                    <input type="url" name="portfolio_url" value={formData.portfolio_url || ''} onChange={handleChange} placeholder="https://yoursite.com" />
                  </div>
                  <div className="prof-actions">
                    <button className="btn" onClick={handleFinishOnboarding} disabled={saving}>
                      {saving ? <><span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Saving…</> : 'Finish & Go to Dashboard →'}
                    </button>
                    <button className="btn btn-secondary" onClick={handleFinishOnboarding}>Skip &amp; Go to Dashboard</button>
                  </div>
                </div>
              )}
            </div>

          ) : (

            /* ── RETURNING USER PROFILE ── */
            <>
              {/* Profile Header */}
              <div className="prof-header">
                <div className="prof-header-avatar">{userInitial}</div>
                <div className="prof-header-info">
                  <div className="prof-header-name">{formData.name || 'Your Name'}</div>
                  <div className="prof-header-meta">
                    {formData.college}{formData.year ? ` · ${formData.year}` : ''}{formData.department ? ` · ${formData.department}` : ''}
                  </div>
                  <div className="prof-comp-bar-row">
                    <div className="prof-comp-bar"><div className="prof-comp-fill" style={{ width: `${pct}%` }} /></div>
                    <span className="prof-comp-pct-label">{pct}% complete</span>
                  </div>
                </div>
                <div className={`prof-save-status prof-save-${saveStatus}`}>
                  {saveStatus === 'saving' && <><span className="prof-save-spin" />Saving…</>}
                  {saveStatus === 'retrying' && <><span className="prof-save-spin" />Retrying…</>}
                  {saveStatus === 'saved' && <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>Saved</>}
                  {saveStatus === 'error' && <>Unable to save</>}
                  {saveStatus === 'pending' && <>Unsaved changes</>}
                </div>
              </div>

              {/* Completion widget */}
              {pct < 100 && (
                <div className="prof-completion-card">
                  <div className="prof-completion-top">
                    <div>
                      <div className="prof-completion-title">Complete your profile</div>
                      <div className="prof-completion-sub">A complete profile gets 3× more recruiter views</div>
                    </div>
                    <div className="prof-completion-ring">
                      <svg width="56" height="56" viewBox="0 0 56 56">
                        <circle cx="28" cy="28" r="23" fill="none" stroke="var(--border)" strokeWidth="4" />
                        <circle cx="28" cy="28" r="23" fill="none" stroke="var(--primary)" strokeWidth="4"
                          strokeDasharray={`${2 * Math.PI * 23}`}
                          strokeDashoffset={`${2 * Math.PI * 23 * (1 - pct / 100)}`}
                          strokeLinecap="round" transform="rotate(-90 28 28)"
                          style={{ transition: 'stroke-dashoffset 0.5s ease' }} />
                      </svg>
                      <span className="prof-ring-pct">{pct}%</span>
                    </div>
                  </div>
                  {incomplete.length > 0 && (
                    <div className="prof-completion-missing">
                      {incomplete.slice(0, 5).map((f, i) => (
                        <div key={i} className="prof-missing-item">
                          <span className="prof-missing-dot" />{f.label}
                        </div>
                      ))}
                      {incomplete.length > 5 && <div className="prof-missing-item prof-missing-more">+{incomplete.length - 5} more</div>}
                    </div>
                  )}
                </div>
              )}

              {student.screening_status === 'Pending' && (
                <div className="alert alert-info" style={{ marginBottom: 20 }}>Your profile is under review. You'll become searchable once verified.</div>
              )}

              {/* Basic Information */}
              <div className="prof-section">
                <div className="prof-section-header">
                  <div className="prof-section-icon">👤</div>
                  <h3>Basic Information</h3>
                </div>
                <div className="prof-field-grid">
                  <div className="form-group prof-field">
                    <label>Full name</label>
                    <input type="text" name="name" value={formData.name || ''} onChange={handleChange} placeholder="Your full name" />
                  </div>
                  <div className="form-group prof-field">
                    <label>Phone number</label>
                    <input type="tel" name="phone" value={formData.phone || ''} onChange={handleChange} placeholder="+91 98765 43210" />
                  </div>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>About you</label>
                  <textarea name="bio" value={formData.bio || ''} onChange={handleChange} rows={4}
                    placeholder="Short intro about your background, interests, and the opportunities you're looking for…" />
                </div>
              </div>

              {/* Availability & Stipend */}
              <div className="prof-section">
                <div className="prof-section-header">
                  <div className="prof-section-icon">⏱</div>
                  <h3>Availability &amp; Stipend</h3>
                </div>
                <div className="form-group">
                  <label>Hours available per week</label>
                  <input type="number" name="weekly_commitment" value={formData.weekly_commitment || ''} onChange={handleChange} placeholder="e.g. 20" min={1} max={80} style={{ maxWidth: 180 }} />
                </div>
                <div className="form-group">
                  <label>When can you start?</label>
                  <div className="avail-grid">
                    {NOTICE_PERIOD_OPTIONS.map(opt => (
                      <label key={opt} className={`avail-option${noticePeriod === opt ? ' selected' : ''}`}>
                        <input type="radio" name="notice_period" checked={noticePeriod === opt} onChange={() => { setFormData(prev => ({ ...prev, notice_period: opt })); scheduleAutosave(); }} />
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ opacity: noticePeriod === opt ? 1 : 0.3, flexShrink: 0 }}><polyline points="20 6 9 17 4 12"/></svg>
                        {opt}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label>Work mode preference</label>
                  <div className="avail-grid">
                    {WORK_MODE_OPTIONS.map(opt => (
                      <label key={opt} className={`avail-option${workModeArray.includes(opt) ? ' selected' : ''}`}>
                        <input type="checkbox" checked={workModeArray.includes(opt)} onChange={() => {
                          setFormData(prev => {
                            const cur = Array.isArray(prev.work_mode) ? prev.work_mode : [];
                            return { ...prev, work_mode: cur.includes(opt) ? cur.filter(o => o !== opt) : [...cur, opt] };
                          });
                          scheduleAutosave();
                        }} />
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ opacity: workModeArray.includes(opt) ? 1 : 0.3, flexShrink: 0 }}><polyline points="20 6 9 17 4 12"/></svg>
                        {opt}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Expected monthly stipend</label>
                  <CompensationRange minVal={rateMin} maxVal={rateMax}
                    onMinChange={v => { setFormData(prev => ({ ...prev, monthly_rate_min: v })); scheduleAutosave(); }}
                    onMaxChange={v => { setFormData(prev => ({ ...prev, monthly_rate_max: v })); scheduleAutosave(); }} />
                </div>
              </div>

              {/* Resume */}
              <div className="prof-section" id="resume-section">
                <div className="prof-section-header">
                  <div className="prof-section-icon">📄</div>
                  <h3>Resume</h3>
                </div>
                {!formData.resume_url && (
                  <div className="prof-empty-state">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                    <div className="prof-empty-text">No resume uploaded</div>
                    <div className="prof-empty-sub">Upload your resume to increase recruiter visibility</div>
                  </div>
                )}
                <div
                  className={`upload-zone${dragOver ? ' drag-over' : ''}`}
                  onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={e => { e.preventDefault(); setDragOver(false); handleFileUpload(e.dataTransfer.files[0]); }}
                  onClick={() => document.getElementById('resume-file').click()}
                >
                  <input id="resume-file" type="file" accept=".pdf" onChange={e => handleFileUpload(e.target.files[0])} disabled={uploading} style={{ display: 'none' }} />
                  <div className="upload-zone-icon">
                    {uploading ? <span className="spinner" style={{ display: 'inline-block' }} /> : <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="1.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>}
                  </div>
                  <div className="upload-zone-title">{uploading ? 'Uploading…' : formData.resume_url ? 'Replace resume' : 'Click to upload or drag & drop'}</div>
                  <div className="upload-zone-sub">PDF only · max 5 MB</div>
                </div>
                {formData.resume_url && (
                  <div className="resume-current">
                    <div className="resume-current-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg></div>
                    <div className="resume-current-info"><div className="resume-current-name">Resume uploaded</div><div className="resume-current-sub">Your current resume is live on your profile</div></div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn btn-primary btn-sm" onClick={e => { e.stopPropagation(); navigate('/student/resume-score'); }}>AI Score</button>
                      <a href={`${API_URL}${formData.resume_url.replace('/uploads/', '/api/uploads/')}`} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm" onClick={e => e.stopPropagation()}>View</a>
                      <button className="btn btn-danger btn-sm" onClick={e => { e.stopPropagation(); handleDeleteResume(); }} disabled={deletingResume}>{deletingResume ? '…' : 'Delete'}</button>
                    </div>
                  </div>
                )}
              </div>

              {/* Skills */}
              <div className="prof-section">
                <div className="prof-section-header">
                  <div className="prof-section-icon">🛠</div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 1 }}>
                    <h3>Skills</h3>
                    <span style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 500 }}>{pendingSkillNames.length} / 10</span>
                  </div>
                </div>
                {pendingSkillNames.length === 0 && (
                  <div className="prof-empty-state" style={{ marginBottom: 16 }}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5M2 12l10 5 10-5"/></svg>
                    <div className="prof-empty-text">No skills added yet</div>
                    <div className="prof-empty-sub">Add skills to power recruiter matching</div>
                  </div>
                )}
                {pendingSkillNames.length > 0 && (
                  <div className="selected-skills-box">
                    {pendingSkillNames.map(name => (
                      <span key={name} className="selected-skill-tag">{name}<button onClick={() => handleTogglePendingSkill(name)}>×</button></span>
                    ))}
                  </div>
                )}
                <div style={{ marginBottom: 14 }}>
                  <input type="text" placeholder="Search skills…" value={skillSearch} onChange={e => setSkillSearch(e.target.value)}
                    style={{ width: '100%', padding: '9px 14px', border: '1.5px solid var(--border)', borderRadius: 'var(--radius)', fontSize: 14, fontFamily: 'var(--font)' }} />
                </div>
                <div className="skill-pool">
                  {filteredSkills.length === 0
                    ? <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>No skills match "{skillSearch}"</span>
                    : filteredSkills.map(skill => {
                      const isSelected = pendingSkillNames.includes(skill.name);
                      const isDisabled = !isSelected && pendingSkillNames.length >= 10;
                      return (
                        <button key={skill._id} className={`skill-chip${isSelected ? ' selected' : ''}${isDisabled ? ' disabled' : ''}`}
                          onClick={() => !isDisabled && handleTogglePendingSkill(skill.name)} type="button">
                          {isSelected && <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                          {skill.name}
                        </button>
                      );
                    })
                  }
                </div>
              </div>

              {/* Profile Links */}
              <div className="prof-section">
                <div className="prof-section-header">
                  <div className="prof-section-icon">🔗</div>
                  <h3>Profile Links</h3>
                </div>
                <div className="prof-field-grid">
                  <div className="form-group prof-field">
                    <label>LinkedIn</label>
                    <input type="url" name="linkedin_url" value={formData.linkedin_url || ''} onChange={handleChange} placeholder="https://linkedin.com/in/yourname" />
                  </div>
                  <div className="form-group prof-field">
                    <label>GitHub</label>
                    <input type="url" name="github_url" value={formData.github_url || ''} onChange={handleChange} placeholder="https://github.com/yourname" />
                  </div>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Portfolio</label>
                  <input type="url" name="portfolio_url" value={formData.portfolio_url || ''} onChange={handleChange} placeholder="https://yoursite.com" />
                </div>
              </div>

              {/* Verification card */}
              {(() => {
                const status = student.screening_status;
                const isComplete = pct === 100;
                if (status === 'Verified') return null;
                return (
                  <div style={{ marginBottom: 24, background: status === 'Pending' ? '#EFF6FF' : isComplete ? '#F0FDF4' : '#FFFBEB', borderRadius: 14, padding: '20px 24px', border: `1px solid ${status === 'Pending' ? '#BFDBFE' : isComplete ? '#BBF7D0' : '#FDE68A'}` }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                      <div style={{ fontSize: 22 }}>{status === 'Pending' ? '⏳' : status === 'Rejected' ? '❌' : isComplete ? '✅' : '📋'}</div>
                      <div style={{ flex: 1 }}>
                        {status === 'Pending' && <><div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Verification Under Review</div><div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Being reviewed by our admin team (1–2 business days).</div></>}
                        {status === 'Rejected' && <><div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Verification Rejected</div><div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>Update your profile and re-apply.</div>{isComplete ? <button className="btn btn-sm" onClick={handleRequestVerification} disabled={requestingVerification}>{requestingVerification ? 'Submitting…' : 'Re-apply for Verification'}</button> : null}</>}
                        {status === 'Under Review' && isComplete && <><div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Ready to Apply for Verification</div><div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>Apply for admin verification to become searchable by recruiters.</div><button className="btn btn-sm" onClick={handleRequestVerification} disabled={requestingVerification}>{requestingVerification ? 'Submitting…' : 'Apply for Verification'}</button></>}
                        {status === 'Under Review' && !isComplete && <><div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Complete Your Profile</div><div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Fill in all required fields above to apply for verification.</div></>}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </>
          )}
        </div>
      )}
    </div>
  );
}
